import { useEffect, useMemo, useRef, useState } from "react";
import { Headphones, Play, Pause, Square, Type, X, Gauge } from "lucide-react";

/**
 * AccessibleReader — leitor de voz nativo (Web Speech API) + modo
 * dislexia. Tudo client-side, sem custo de API externa.
 *
 * Props:
 *   - targetRef: React ref para o nó que contém o texto do artigo.
 *                Lemos `innerText` no momento do play (não cacheamos
 *                para sempre pegar o conteúdo final após render).
 *   - title: opcional, lido como preâmbulo antes do corpo do artigo.
 *
 * Detalhes técnicos:
 *   - O catálogo de vozes do navegador carrega assíncrono. Usamos
 *     `onvoiceschanged` para garantir que pegamos uma voz em pt-BR
 *     antes de tocar.
 *   - Browsers limitam o tamanho de cada utterance (Chrome corta em
 *     ~200-300 chars em alguns casos). Quebramos o texto em frases e
 *     enfileiramos — quando uma termina, dispara a próxima.
 *   - `synth.cancel()` no unmount garante que sair da página para o
 *     áudio (não toca em background).
 *   - Toggle de fonte dislexia adiciona `.font-dyslexic` na div alvo.
 *     A @font-face do OpenDyslexic é injetada via <link> sob demanda
 *     (não polui o CSS inicial).
 */
export default function AccessibleReader({ targetRef, title }) {
  // Capacidade — alguns browsers (Firefox em iOS, navegadores muito
  // antigos) não expõem speechSynthesis. Esconde silenciosamente.
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [rate, setRate] = useState(1);
  const [dyslexic, setDyslexic] = useState(false);
  const [voicesReady, setVoicesReady] = useState(false);

  const voiceRef = useRef(null);
  // Fila de frases pendentes + índice atual — usado pelo encadeamento
  // de utterances (uma frase por vez para driblar o limite de chars).
  const queueRef = useRef([]);
  const cursorRef = useRef(0);
  // Flag para distinguir "fim natural" (avança fila) de "cancel
  // manual" (não avança). Sem isso, clicar Stop dispara onend e o
  // resto da fila vaza.
  const stoppedRef = useRef(false);
  // Cache do innerHTML do alvo ANTES de envolvermos cada sentença em
  // <span data-juris-sentence="N">. Restauramos no Stop / unmount /
  // mudança de rate, garantindo que o DOM volta ao estado original
  // (com links, marcações, imagens etc) intacto.
  const originalHtmlRef = useRef(null);

  // 1) Carrega o catálogo de vozes do SO/browser. O array fica vazio
  //    no primeiro tick em quase todos os navegadores; espera-se o
  //    evento `voiceschanged`. Em Safari iOS o evento pode não
  //    disparar — caímos no fallback de polling de 1s.
  useEffect(() => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    const pickVoice = () => {
      const voices = synth.getVoices();
      if (!voices || voices.length === 0) return false;
      // Preferimos uma voz pt-BR feminina/nativa. Se não houver,
      // qualquer pt serve; se nem isso, deixamos o default do SO.
      const preferNames = [
        "Maria", "Francisca", "Luciana", "Helena",
        "Google português do Brasil", "Microsoft Maria",
      ];
      const ptBR = voices.filter((v) => /^pt[-_]BR/i.test(v.lang));
      const pt = voices.filter((v) => v.lang && v.lang.toLowerCase().startsWith("pt"));
      let chosen =
        ptBR.find((v) => preferNames.some((n) => v.name.includes(n))) ||
        ptBR[0] ||
        pt.find((v) => preferNames.some((n) => v.name.includes(n))) ||
        pt[0] ||
        null;
      voiceRef.current = chosen;
      setVoicesReady(true);
      return true;
    };
    if (pickVoice()) return;
    const handler = () => pickVoice();
    synth.addEventListener("voiceschanged", handler);
    // Fallback polling (Safari iOS).
    const id = setInterval(() => { if (pickVoice()) clearInterval(id); }, 600);
    setTimeout(() => clearInterval(id), 5000);
    return () => {
      synth.removeEventListener("voiceschanged", handler);
      clearInterval(id);
    };
  }, [supported]);

  // 2) Cleanup global — quando o componente desmonta (mudou de
  //    artigo, navegou para outra rota) interrompemos qualquer fala
  //    e restauramos o HTML original do alvo (remove os spans de
  //    sentença que injetamos).
  useEffect(() => {
    const node = targetRef?.current;
    return () => {
      if (supported) {
        stoppedRef.current = true;
        try { window.speechSynthesis.cancel(); } catch (_) { /* ignore */ }
      }
      if (node && originalHtmlRef.current !== null) {
        node.innerHTML = originalHtmlRef.current;
        originalHtmlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  // 3) Toggle do modo dislexia — aplica classe na div do artigo.
  //    Carrega a @font-face do OpenDyslexic sob demanda (uma vez).
  useEffect(() => {
    if (!targetRef?.current) return;
    if (dyslexic) {
      targetRef.current.classList.add("font-dyslexic");
      // Injeta o <link> do OpenDyslexic apenas uma vez por sessão.
      if (!document.getElementById("opendyslexic-cdn")) {
        const link = document.createElement("link");
        link.id = "opendyslexic-cdn";
        link.rel = "stylesheet";
        link.href = "https://fonts.cdnfonts.com/css/open-dyslexic";
        document.head.appendChild(link);
      }
    } else {
      targetRef.current.classList.remove("font-dyslexic");
    }
  }, [dyslexic, targetRef]);

  // Envolve cada sentença do alvo em <span data-juris-sentence="N">
  // mutando o DOM (in-place). Trabalha em text nodes para preservar
  // todo o markup intermediário (links, imagens, listas, blockquotes).
  // Retorna a lista de sentenças extraídas, na mesma ordem dos índices
  // injetados — usada para construir as utterances depois.
  const wrapSentencesInDom = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Ignora text nodes vazios ou só com whitespace.
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        // Não re-envolve dentro de spans nossos.
        if (node.parentElement && node.parentElement.hasAttribute("data-juris-sentence")) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);
    const sentences = [];
    let idx = 0;
    textNodes.forEach((tn) => {
      const text = tn.nodeValue;
      const parts = text.split(/(?<=[.!?…])\s+/);
      const frag = document.createDocumentFragment();
      parts.forEach((p, i) => {
        if (!p) return;
        const span = document.createElement("span");
        span.setAttribute("data-juris-sentence", String(idx));
        // Mantém o espaço entre frases dentro do span para o fluxo
        // de texto continuar idêntico ao original.
        span.textContent = i < parts.length - 1 ? p + " " : p;
        frag.appendChild(span);
        sentences.push(p.trim());
        idx++;
      });
      if (tn.parentNode) tn.parentNode.replaceChild(frag, tn);
    });
    return sentences;
  };

  const highlightRange = (from, to) => {
    const node = targetRef?.current;
    if (!node || from < 0) return;
    // Remove qualquer destaque anterior + aplica no novo intervalo.
    node.querySelectorAll(".juris-reading-now").forEach((el) => el.classList.remove("juris-reading-now"));
    for (let i = from; i <= to; i++) {
      const el = node.querySelector(`[data-juris-sentence="${i}"]`);
      if (el) el.classList.add("juris-reading-now");
    }
    // Auto-scroll suave para a primeira sentença do trecho atual.
    const firstEl = node.querySelector(`[data-juris-sentence="${from}"]`);
    if (firstEl && typeof firstEl.scrollIntoView === "function") {
      firstEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const clearHighlights = () => {
    const node = targetRef?.current;
    if (!node) return;
    node.querySelectorAll(".juris-reading-now").forEach((el) => el.classList.remove("juris-reading-now"));
  };

  const restoreOriginalHtml = () => {
    const node = targetRef?.current;
    if (node && originalHtmlRef.current !== null) {
      node.innerHTML = originalHtmlRef.current;
      originalHtmlRef.current = null;
    }
  };

  // Quebra o texto em "frases" curtas. Mantemos sentenças entre
  // 30 e 280 chars — abaixo disso a leitura fica fragmentada demais,
  // acima alguns navegadores cortam. Retorna itens da forma
  // { text, from, to } onde from/to apontam para os índices das
  // <span data-juris-sentence> injetadas no DOM, permitindo o
  // destaque sincronizado por utterance.
  const buildQueue = useMemo(() => () => {
    const node = targetRef?.current;
    if (!node) return [];
    // Cacheia o HTML original UMA vez por sessão de leitura. Replays
    // (mudança de velocidade) re-aproveitam o snapshot — a fila e os
    // spans permanecem válidos.
    if (originalHtmlRef.current === null) {
      originalHtmlRef.current = node.innerHTML;
    } else {
      // Re-build a partir do snapshot original (limpa spans antigos).
      node.innerHTML = originalHtmlRef.current;
    }
    const sentences = wrapSentencesInDom(node);
    const items = [];
    // Preâmbulo (título). Não tem span no DOM — from = -1 sinaliza
    // ao highlightRange para ignorar (nada para destacar).
    if (title) items.push({ text: `${title}.`, from: -1, to: -1 });
    let buf = "", fromIdx = 0;
    sentences.forEach((s, i) => {
      if ((buf + " " + s).trim().length < 220) {
        if (!buf) fromIdx = i;
        buf = (buf + " " + s).trim();
      } else {
        if (buf) items.push({ text: buf, from: fromIdx, to: i - 1 });
        buf = s;
        fromIdx = i;
      }
    });
    if (buf) items.push({ text: buf, from: fromIdx, to: sentences.length - 1 });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, title]);

  const speakNext = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (cursorRef.current >= queueRef.current.length) {
      setPlaying(false);
      setPaused(false);
      clearHighlights();
      return;
    }
    const item = queueRef.current[cursorRef.current];
    const u = new SpeechSynthesisUtterance(item.text);
    if (voiceRef.current) u.voice = voiceRef.current;
    u.lang = (voiceRef.current && voiceRef.current.lang) || "pt-BR";
    u.rate = rate;
    u.pitch = 1;
    u.onstart = () => {
      if (stoppedRef.current) return;
      highlightRange(item.from, item.to);
    };
    u.onend = () => {
      if (stoppedRef.current) return;
      cursorRef.current += 1;
      speakNext();
    };
    u.onerror = () => {
      if (stoppedRef.current) return;
      cursorRef.current += 1;
      speakNext();
    };
    synth.speak(u);
  };

  const handlePlay = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (synth.speaking && synth.paused) {
      synth.resume();
      setPaused(false);
      return;
    }
    if (synth.speaking) return; // já tocando
    // Reinicia: monta fila a partir do texto atual.
    stoppedRef.current = false;
    queueRef.current = buildQueue();
    cursorRef.current = 0;
    if (queueRef.current.length === 0) return;
    setPlaying(true);
    setPaused(false);
    speakNext();
  };

  const handlePause = () => {
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (synth.speaking && !synth.paused) {
      synth.pause();
      setPaused(true);
    }
  };

  const handleStop = () => {
    if (!supported) return;
    stoppedRef.current = true;
    window.speechSynthesis.cancel();
    setPlaying(false);
    setPaused(false);
    cursorRef.current = 0;
    clearHighlights();
    restoreOriginalHtml();
  };

  const changeRate = (newRate) => {
    setRate(newRate);
    // Se estiver tocando, reinicia da frase atual com a nova
    // velocidade (Web Speech API não permite mudar rate em runtime).
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (synth.speaking) {
      stoppedRef.current = true;
      synth.cancel();
      // pequeno delay para o cancel propagar antes do novo speak
      setTimeout(() => {
        stoppedRef.current = false;
        const continueFromCursor = () => {
          if (cursorRef.current >= queueRef.current.length) {
            setPlaying(false); setPaused(false); clearHighlights(); return;
          }
          const it = queueRef.current[cursorRef.current];
          const u = new SpeechSynthesisUtterance(it.text);
          if (voiceRef.current) u.voice = voiceRef.current;
          u.lang = (voiceRef.current && voiceRef.current.lang) || "pt-BR";
          u.rate = newRate;
          u.onstart = () => { if (!stoppedRef.current) highlightRange(it.from, it.to); };
          u.onend = () => {
            if (stoppedRef.current) return;
            cursorRef.current += 1;
            continueFromCursor();
          };
          u.onerror = u.onend;
          synth.speak(u);
        };
        continueFromCursor();
      }, 80);
    }
  };

  if (!supported) return null;

  return (
    <>
      {/* Trigger inline — barra discreta logo abaixo do título do
          artigo. Sempre visível, abre o painel flutuante ao clicar. */}
      <button
        type="button"
        onClick={() => { setOpen(true); if (!playing) handlePlay(); }}
        className="no-print inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200 text-sm font-medium transition min-w-[200px] justify-center"
        data-testid="accessible-reader-inline-trigger"
        aria-label="Ouvir este artigo"
      >
        <Headphones className="w-4 h-4" />
        Ouvir este artigo
        {!voicesReady && <span className="text-[10px] text-sky-300/60">(carregando voz…)</span>}
      </button>

      {/* Floating player — bottom-right. Abre quando o usuário clica
          no trigger inline OU quando começa a tocar. Persistente até
          o usuário fechar manualmente (X) ou sair da página. */}
      {(open || playing) && (
        <div
          className="no-print fixed bottom-4 right-4 z-50 w-[300px] rounded-xl border border-sky-500/40 bg-slate-950/95 backdrop-blur shadow-2xl shadow-sky-900/30 p-3"
          role="region"
          aria-label="Leitor acessível"
          data-testid="accessible-reader-floating"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sky-200 text-xs font-semibold uppercase tracking-wider">
              <Headphones className="w-3.5 h-3.5" />
              Leitor acessível
            </div>
            <button
              type="button"
              onClick={() => { handleStop(); setOpen(false); }}
              className="text-slate-400 hover:text-white"
              aria-label="Fechar leitor"
              data-testid="accessible-reader-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 mb-2">
            {!playing || (playing && paused) ? (
              <button
                type="button"
                onClick={handlePlay}
                disabled={!voicesReady}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-950 text-sm font-semibold disabled:opacity-40"
                data-testid="accessible-reader-play"
              >
                <Play className="w-4 h-4 fill-current" />
                {paused ? "Continuar" : "Ouvir"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handlePause}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-semibold"
                data-testid="accessible-reader-pause"
              >
                <Pause className="w-4 h-4 fill-current" />
                Pausar
              </button>
            )}
            <button
              type="button"
              onClick={handleStop}
              disabled={!playing}
              className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-30"
              aria-label="Parar leitura"
              data-testid="accessible-reader-stop"
            >
              <Square className="w-4 h-4 fill-current" />
            </button>
          </div>

          {/* Velocidade */}
          <div className="mb-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              <Gauge className="w-3 h-3" /> Velocidade
            </div>
            <div className="grid grid-cols-4 gap-1">
              {[0.8, 1, 1.25, 1.5].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => changeRate(r)}
                  className={`px-1.5 py-1 rounded text-xs font-mono transition ${
                    rate === r
                      ? "bg-sky-500 text-slate-950 font-bold"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-300"
                  }`}
                  data-testid={`accessible-reader-rate-${r}`}
                >
                  {r}×
                </button>
              ))}
            </div>
          </div>

          {/* Toggle fonte dislexia */}
          <button
            type="button"
            onClick={() => setDyslexic((d) => !d)}
            className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition border ${
              dyslexic
                ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200"
                : "bg-slate-900 border-slate-700 hover:border-slate-600 text-slate-300"
            }`}
            data-testid="accessible-reader-dyslexic-toggle"
            aria-pressed={dyslexic}
          >
            <Type className="w-3.5 h-3.5" />
            Fonte para dislexia: {dyslexic ? "ON" : "OFF"}
          </button>
          <p className="text-[10px] text-slate-500 leading-snug mt-2">
            Voz, velocidade e fonte processadas pelo seu próprio navegador. Nada
            é enviado para servidores.
          </p>
        </div>
      )}
    </>
  );
}
